/**
 * A payment run: the approved invoices finance pays together on one date
 * (spec §5 handoff 3, subsystem 2).
 *
 * The run is the unit the bank file is produced from (D8 — we produce a file,
 * we do not move money). Approval is the only route in, so a run can only ever
 * contain invoices business has already approved.
 *
 * Pure. The caller fetches the invoices and passes them in.
 */

import { netPayable, roundMoney } from './tax';

/** Only an approved invoice can enter a run. */
export const PAYABLE_STATUS = 'approved';

export const RUN_STATES = {
  DRAFT: 'draft',
  EXPORTED: 'exported',
  SETTLED: 'settled',
};

export const RUN_STATE_LABELS = {
  [RUN_STATES.DRAFT]: 'Draft',
  [RUN_STATES.EXPORTED]: 'File produced',
  [RUN_STATES.SETTLED]: 'Settled',
};

/** Why a member cannot, or perhaps should not, be paid. */
export const BLOCKER = {
  NO_PAYOUT: 'no_payout',
  ZERO_NET: 'zero_net',
  ESTIMATED: 'estimated',
  DUPLICATE: 'duplicate',
  WITHDRAWN: 'withdrawn',
};

/** The payout fields as the creator's bank detail record carries them. */
const PAYOUT_FIELDS = [
  'account_holder_name',
  'account_number',
  'ifsc_code',
  'bank_name',
  'pan_card_no',
  'gst_number',
  'msme_udyam_number',
];

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

const text = (value) => (isBlank(value) ? '' : String(value).trim());

/**
 * The payee behind an invoice, whichever shape the API used.
 *
 * `pan` and `supplier_gstin` are mapped across from the payout field names so
 * the tax computation can read one record.
 */
export function payeeOf(invoice) {
  const record = invoice || {};
  const source = record.payout_details || record.bank_detail || record;

  const payee = PAYOUT_FIELDS.reduce((carry, field) => {
    if (isBlank(source[field])) return carry;
    return { ...carry, [field]: text(source[field]) };
  }, {});

  return {
    ...payee,
    ...(payee.pan_card_no ? { pan: payee.pan_card_no } : {}),
    ...(payee.gst_number ? { supplier_gstin: payee.gst_number } : {}),
  };
}

/**
 * Shown when nothing on the record names anyone. A display fallback only —
 * `bankFile` treats it as no name at all, because it is not a beneficiary.
 */
export const NAME_UNKNOWN = 'Unnamed';

/** The name to pay: the vendor, else the creator, else the account holder. */
function nameOf(invoice, payee) {
  const record = invoice || {};
  return (
    text(record.vendor_name) ||
    text(record.influencer_name) ||
    text(payee.account_holder_name) ||
    NAME_UNKNOWN
  );
}

/** One line of a run: who, how much, and whether the figure is trustworthy. */
export function memberOf(invoice) {
  const record = invoice || {};
  const payee = payeeOf(record);
  // The invoice's own identifiers win: they are what finance entered against
  // this invoice. The payout record is the fallback for an invoice that has
  // none yet. Spreading the payee over the record would wipe the entered
  // values with undefined, and a missing PAN computes TDS at 20%.
  const tax = netPayable({
    ...record,
    pan: record.pan || payee.pan,
    supplier_gstin: record.supplier_gstin || payee.supplier_gstin,
  });

  return {
    invoice_id: record.id,
    line_status: record.line_status,
    invoice_number: text(record.invoice_number),
    name: nameOf(record, payee),
    status: record.status,
    campaign_name: text(record.campaign_name),
    payee,
    taxable: tax.taxable,
    gst: tax.gst.total,
    gst_kind: tax.gst.kind,
    tds: tax.tds.amount,
    tds_section: tax.tds.section,
    net: tax.net,
    estimated: tax.estimated,
  };
}

/**
 * Whether this invoice's roster line has been withdrawn from the campaign.
 *
 * `line_status` is new on the dashboard rows (API #2977). Absent on an older
 * build, and absence must not read as withdrawn -- that would empty the
 * payment run panel rather than trim it.
 */
const onWithdrawnLine = (invoice) =>
  String(invoice.line_status ?? '').trim().toLowerCase() === 'withdrawn';

/**
 * The invoices that may go into a run — approved, on a line that is still on
 * the campaign, and not already in another run. `runId` keeps the members of
 * the run being edited eligible.
 *
 * The withdrawn check is not cosmetic. The server refuses an entire run that
 * contains one of these, so without it finance assembles a run, hits export,
 * and loses all of it at the moment they are trying to pay people.
 */
export function eligibleForRun(invoices, runId = null) {
  const list = Array.isArray(invoices) ? invoices : [];
  const held = (invoice) =>
    !isBlank(runId) && invoice.payment_run_id === runId;

  return list.filter((invoice) => {
    if (!invoice || invoice.status !== PAYABLE_STATUS) return false;

    // Already in the run being edited. The server checks withdrawal only
    // when a run is created -- /exported does not re-check -- so this one
    // stays in the run and is paid. Dropping it would understate what the
    // run pays and leave it out of the bank file while the money went out
    // anyway, which is the worst of both.
    if (held(invoice)) return true;

    if (onWithdrawnLine(invoice)) return false;
    return isBlank(invoice.payment_run_id);
  });
}

/**
 * Approved invoices that no run can take, because their line was withdrawn.
 *
 * Excluding them from `eligibleForRun` stops the run failing; it also makes
 * them invisible, and an approved invoice is money someone is owed. This
 * hands them back so finance is told rather than left to notice.
 */
export function strandedByWithdrawal(invoices) {
  const list = Array.isArray(invoices) ? invoices : [];
  return list.filter(
    (invoice) =>
      invoice &&
      invoice.status === PAYABLE_STATUS &&
      isBlank(invoice.payment_run_id) &&
      onWithdrawnLine(invoice),
  );
}

/** Column totals, and whether any figure in them is only an estimate. */
export function runTotals(members) {
  const list = Array.isArray(members) ? members : [];
  return list.reduce(
    (totals, member) => ({
      count: totals.count + 1,
      taxable: roundMoney(totals.taxable + member.taxable),
      gst: roundMoney(totals.gst + member.gst),
      tds: roundMoney(totals.tds + member.tds),
      net: roundMoney(totals.net + member.net),
      estimated: totals.estimated || Boolean(member.estimated),
    }),
    { count: 0, taxable: 0, gst: 0, tds: 0, net: 0, estimated: false },
  );
}

/**
 * Assemble a run. `ids` picks and orders the members; without it every
 * eligible invoice goes in. Nothing is mutated — a new run comes back.
 */
export function buildRun({ invoices, ids, date = '', reference = '', runId = null } = {}) {
  const eligible = eligibleForRun(invoices, runId);

  const chosen = Array.isArray(ids)
    ? ids.map((id) => eligible.find((invoice) => invoice.id === id)).filter(Boolean)
    : eligible;

  const members = chosen.map(memberOf);
  return { date, reference, members, totals: runTotals(members), state: RUN_STATES.DRAFT };
}

/** Add a member that is out, or remove one that is in. Returns a new run. */
export function toggleMember(run, invoices, invoiceId) {
  const current = (run && run.members) || [];
  const present = current.some((member) => member.invoice_id === invoiceId);

  const ids = present
    ? current.filter((member) => member.invoice_id !== invoiceId).map((m) => m.invoice_id)
    : [...current.map((m) => m.invoice_id), invoiceId];

  return { ...buildRun({ invoices, ids, date: run?.date, reference: run?.reference }), state: run?.state || RUN_STATES.DRAFT };
}

/**
 * What stands between this member and a payment.
 *
 * `stop` keeps the line out of the bank file — there is nowhere to send the
 * money. `warn` lets it through but says so, because a figure estimated from
 * `amount` is not a tax computation.
 */
export function blockersFor(member) {
  const line = member || {};
  const payee = line.payee || {};
  const at = (code, severity, message) => ({
    code,
    severity,
    message,
    invoice_id: line.invoice_id,
    name: line.name,
  });

  const blockers = [];

  if (isBlank(payee.account_number) || isBlank(payee.ifsc_code)) {
    blockers.push(at(BLOCKER.NO_PAYOUT, 'stop', 'No bank account and IFSC on file.'));
  }

  if (!(line.net > 0)) {
    blockers.push(at(BLOCKER.ZERO_NET, 'stop', 'Net payable is zero.'));
  }

  if (line.estimated) {
    blockers.push(
      at(BLOCKER.ESTIMATED, 'warn', 'No taxable value on the invoice — the net is estimated from the amount.'),
    );
  }

  // Warn rather than stop. The server keeps an invoice in a run once the run
  // exists and pays it on export, and the money is genuinely owed, so
  // blocking would be wrong. But this is about to send money to someone
  // taken off the campaign, and finance should know before the file goes
  // out rather than after.
  if (String(line.line_status ?? '').trim().toLowerCase() === 'withdrawn') {
    blockers.push(
      at(
        BLOCKER.WITHDRAWN,
        'warn',
        'Their line was withdrawn from the campaign after this run was made. '
        + 'The invoice was approved before that, so it is still owed and will be paid.',
      ),
    );
  }

  return blockers;
}

/**
 * Every blocker across the run, plus the one that only exists between members:
 * the same account receiving the same amount twice, which is what a double
 * payment looks like before it happens.
 */
export function runBlockers(run) {
  const members = (run && run.members) || [];
  const perMember = members.reduce((all, member) => [...all, ...blockersFor(member)], []);

  const seen = new Map();
  const duplicates = members.reduce((all, member) => {
    const account = (member.payee || {}).account_number;
    if (isBlank(account) || !(member.net > 0)) return all;

    const key = `${account}|${member.net}`;
    if (!seen.has(key)) {
      seen.set(key, member);
      return all;
    }

    return [
      ...all,
      {
        code: BLOCKER.DUPLICATE,
        severity: 'warn',
        message: `Same account and same amount as ${seen.get(key).name}.`,
        invoice_id: member.invoice_id,
        name: member.name,
      },
    ];
  }, []);

  return [...perMember, ...duplicates];
}

/** Derived, so it cannot disagree with the invoices underneath it. */
export function runState(run) {
  const record = run || {};
  const members = record.members || [];

  if (members.length > 0 && members.every((member) => member.status === 'paid')) {
    return RUN_STATES.SETTLED;
  }
  if (!isBlank(record.exported_at)) return RUN_STATES.EXPORTED;
  return RUN_STATES.DRAFT;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `{field: message}`; empty means valid. */
export function validateRun(form) {
  const values = form || {};
  const errors = {};

  const date = text(values.date);
  if (!date) {
    errors.date = 'Choose the date the payment goes out.';
  } else if (!ISO_DATE_RE.test(date) || Number.isNaN(new Date(date).getTime())) {
    errors.date = 'Enter the date as YYYY-MM-DD.';
  }

  if (!(values.members || []).length) {
    errors.members = 'Add at least one invoice to the run.';
  }

  return errors;
}

/** The request body. */
export function runPayload(run) {
  const record = run || {};
  const reference = text(record.reference);

  return {
    payment_date: text(record.date),
    ...(reference ? { reference } : {}),
    invoice_ids: (record.members || []).map((member) => member.invoice_id),
  };
}
